<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import {
  MISMATCH_DATA_TYPE,
  TERMS_AND_CONDITIONS_LABEL,
  UNIQUE_KEY_FIELD_KEYS,
} from "@/util/constants";
import {
  CUSTOM_FIELD_NAME_TOOLTIP_LEARN_MORE_URL,
  getCustomFieldNameTooltipKey,
  getCustomFieldNameTooltipLearnMoreKey,
  getCustomObjectName,
  getCustomObjectNamePlural,
  isCustomObjectField,
  isPrimaryField,
  isTrueCustomObjectField,
} from "@/util/customObjectUtils";
import { AlignmentPosition, applyStyles, fieldFullWidth } from "@/util/methods";
// @ts-ignore
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  Edit01Icon,
  InfoCircleIcon,
  Monitor04Icon,
  Phone01Icon,
  PlusIcon,
  Trash01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
// @ts-ignore
import RichTextEditor from "@/components/text-editor/index.vue";
import { getDeviceAware, setDeviceAware } from "@/composition/useDeviceAware";
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { deviceOptions } from "@/util/constants";
import {
  UIAlert,
  UIButton,
  UICheckbox,
  UICollapse,
  UICollapseItem,
  UIDivider,
  UIDropdown,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UISwitch,
  UITabPane,
  UITabs,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { cloneDeep, snakeCase } from "lodash";
import { storeToRefs } from "pinia";
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { CONSTANTS } from "../../util/constants";
import AddressSettings from "../common/Address/AddressSettings.vue";
import ButtonSidebar from "../common/ButtonSidebar.vue";
import Card from "../common/CardComponent.vue";
import CodeEditorModel from "../common/CodeEditorModel.vue";
import ContactAssociationSettingsModal from "../common/ContactAssociationSettingsModal.vue";
import CountInput from "../common/CountInput.vue";
import DateSettings from "../common/DateSettings.vue";
import DropdownEditor from "../common/DropdownEditor.vue";
import EditOpportunitySettingsModal from "../common/EditOpportunitySettingsModal.vue";
import RatingEditor from "../common/ElementEditors/RatingEditor.vue";
import CaptchaVerificationMode from "../common/Elements/CaptchaVerificationMode.vue";
import LabelAlignment from "../common/Elements/LabelAlignment.vue";
import FileUploadEditor from "../common/FileUploadEditor.vue";
import ListEditor from "../common/ListEditor.vue";
import PaymentSettings from "../common/Payments/PaymentSettings.vue";
import RadioSelectEditor from "../common/RadioSelectEditor.vue";
import ScoreEditor from "../common/ScoreEditor.vue";
import MediaCenter from "../common/Sidebar/MediaCenter.vue";
import TextSidebar from "../common/TextSidebar.vue";

const { t } = useI18n();
const store = useAppStore();
const emitter = useEmitter();

const props = defineProps({
  selectedIndex: {
    type: Number,
    default: 0,
  },
});

const isMobile = computed(() => store.deviceMode === "mobile");

const fieldPlaceholder = store.elements[props.selectedIndex]?.placeholder;
const standardTextField = store.elements[props.selectedIndex];
const buttonField = store.elements[props.selectedIndex];
const { getCustomFieldsFromStore } = storeToRefs(store);

const showModal = ref(false);
const code = ref(store.elements[props.selectedIndex]?.html);
const uniqueKeyPlaceholder = ref(
  snakeCase(store.elements[props.selectedIndex]?.label)
);
const uniqueKeyError = ref(false);
const customFieldError = ref(false);
const buttonSidebarRef = ref(null);

const isHiddenFieldDisable = computed(() => {
  const element = store.elements[props.selectedIndex];
  if (!element) {
    return false;
  }
  if (element?.required) {
    return true;
  }
  // Disable hidden for primary fields (custom objects/business primary)
  const primaryInfo = isPrimaryField(element, store.customObjects || []);
  return Boolean(primaryInfo?.isPrimary);
});

const isRequiredFieldDisable = computed(() => {
  if (store.elements[props.selectedIndex]?.hidden) {
    return true;
  }
  return false;
});

// Disable Required checkbox only when field is hidden or a primary field
const isRequiredCheckboxDisabled = computed(() => {
  const element = store.elements[props.selectedIndex];
  if (!element) {
    return isRequiredFieldDisable.value;
  }
  // Lock required for primary fields; allow toggling otherwise
  const primaryInfo = isPrimaryField(element, store.customObjects || []);
  const isPrimary = Boolean(primaryInfo?.isPrimary);
  return isRequiredFieldDisable.value || isPrimary;
});

const showModelUpdate = (modalUpdate: boolean) => {
  showModal.value = modalUpdate;
};

const submitUpdate = (updatedCode: string) => {
  code.value = updatedCode;
  if (store.elements[props.selectedIndex]) {
    store.elements[props.selectedIndex].html = code.value;
  }
  store.anyUnsavedChanges = true;
};

const onClickOfEditHTML = () => {
  showModal.value = !showModal.value;
  code.value = store.elements[props.selectedIndex]?.html;
};

const onBack = () => {
  // Check if we're dealing with a button element and if the theme selector is shown
  if (
    store.elements[props.selectedIndex]?.tag === "button" &&
    buttonSidebarRef.value
  ) {
    // Try to access the showThemeSelector property from the ButtonSidebar component
    const buttonSidebarComponent = buttonSidebarRef.value;
    if (buttonSidebarComponent?.showThemeSelector) {
      // If theme selector is shown, go back to main button sidebar
      buttonSidebarComponent.showThemeSelector = false;
      return;
    }
  }
  store.isAnyElementActive = false;
};

const switchDeviceMode = (option: string) => {
  store.deviceMode = option as any;
};

const getLabel = computed(() => {
  const el = store.elements[props.selectedIndex];
  return el?.label;
});

const getTypeLabel = computed(() => {
  const typeLabel = store.elements[props.selectedIndex]?.typeLabel;
  if (typeLabel) {
    return typeLabel;
  }
  const model = store.elements[props.selectedIndex]?.model;
  if (!model || model === "contact") {
    return "";
  }
  let displayName = "";
  if (model === "business") {
    displayName = t("common.business");
  } else if (model === "opportunity") {
    displayName = t("common.opportunity");
  } else if (isTrueCustomObjectFieldWrapper(model)) {
    displayName = getCustomObjectNamePluralWrapper(model);
  }
  if (!displayName) {
    const seg = (model.split(".").pop() || model) as string;
    displayName = seg.charAt(0).toUpperCase() + seg.slice(1);
  }
  // Ensure first letter is capitalized for consistency (e.g., cars -> Cars)
  if (typeof displayName === "string" && displayName.length > 0) {
    displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
  }
  const prefix = t("common.object") || "Object";
  return `${prefix}: ${displayName}`;
});

const getPlaceholder = computed(() => {
  return store.elements[props.selectedIndex]?.placeholder;
});

const getPlaceholder2 = computed(() => {
  return store.elements[props.selectedIndex]?.placeholder2;
});

const getShortLabel = computed(() => {
  return store.elements[props.selectedIndex]?.shortLabel;
});

const getHiddenQuery = computed(() => {
  return store.elements[props.selectedIndex]?.hiddenFieldQueryKey;
});

const getUniqueKey = computed(() => {
  return store.elements[props.selectedIndex]?.fieldKey;
});

const getRequired = computed(() => {
  return store.elements[props.selectedIndex]?.required;
});

const getHidden = computed(() => {
  return store.elements[props.selectedIndex]?.hidden;
});

const getEnableCountryPicker = computed(() => {
  if (store.elements[props.selectedIndex]?.enableCountryPicker === undefined) {
    return true;
  }
  return store.elements[props.selectedIndex].enableCountryPicker;
});

const enableValidateEmailModel = computed({
  get() {
    return store.elements[props.selectedIndex]?.enableValidateEmail ?? false;
  },
  set(value: boolean) {
    store.elements[props.selectedIndex].enableValidateEmail = value;
    store.anyUnsavedChanges = true;
  },
});

const enableValidatePhoneModel = computed({
  get() {
    return store.elements[props.selectedIndex]?.enableValidatePhone ?? false;
  },
  set(value: boolean) {
    store.elements[props.selectedIndex].enableValidatePhone = value;
    store.anyUnsavedChanges = true;
  },
});

const getHiddenValue = computed(() => {
  return store.elements[props.selectedIndex]?.hiddenFieldValue;
});

const getSource = computed(() => {
  return store.elements[props.selectedIndex]?.value;
});

const getAltImage = computed(() => {
  return store.elements[props.selectedIndex]?.alt;
});

const getSpreadColumns = computed(() => {
  return store.elements[props.selectedIndex]?.spreadColumns;
});

const getNumberOfColumns = computed(() => {
  return store.elements[props.selectedIndex]?.columnsNumber;
});

const getDefaultNumberOfColumns = computed(() => {
  return store.elements[props.selectedIndex]?.picklistOptionsImage
    ? undefined
    : 1;
});

const disableUniqueKey = computed(() => {
  return (
    store.elements[props.selectedIndex]?.customEdited ||
    !store.elements[props.selectedIndex]?.custom ||
    store.elements[props.selectedIndex]?.dataType === "SCORE" ||
    store.elements[props.selectedIndex]?.dataType === "RATING"
  );
});

const getFieldWidthPercentage = (): number => {
  if (!store.elements[props.selectedIndex]?.fieldWidthPercentage) {
    const layout =
      store.deviceMode === "mobile"
        ? store.builderConfig.mobileLayout || store.builderConfig.layout
        : store.builderConfig.layout;

    if (layout === CONSTANTS.ONE_COLUMN) {
      store.elements[props.selectedIndex].fieldWidthPercentage = 100;
    } else if (
      layout === CONSTANTS.TWO_COLUMN &&
      !fieldFullWidth(store.elements[props.selectedIndex]?.type)
    ) {
      store.elements[props.selectedIndex].fieldWidthPercentage = 50;
    } else if (layout === CONSTANTS.SINGLE_LINE) {
      store.elements[props.selectedIndex].fieldWidthPercentage = parseFloat(
        (100 / store.elements.length).toFixed(2)
      );
    }
  }
  return store.elements[props.selectedIndex]?.fieldWidthPercentage ?? 100;
};

const handleImageWidth = (val) => {
  store.elements[props.selectedIndex].width = val;
  store.anyUnsavedChanges = true;
};

const handleImageHeight = (val) => {
  store.elements[props.selectedIndex].height = val;
  store.anyUnsavedChanges = true;
};

const handleTextColor = (val: string) => {
  store.elements[props.selectedIndex].textColor = val;
  store.anyUnsavedChanges = true;
};

const handleLinkColor = (val: string) => {
  store.elements[props.selectedIndex].linkColor = val;
  store.anyUnsavedChanges = true;
};

const alignImage = (align) => {
  store.elements[props.selectedIndex].align = align;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};

const handleTextUpdate = (output) => {
  //To Maintain Reactivity of Store
  Object.assign(store.elements[props.selectedIndex], output);
};
const handleButtonUpdate = (output) => {
  //To Maintain Reactivity of Store
  Object.assign(store.elements[props.selectedIndex], output);
};
const updatePreview = (element) => {
  const content = element.placeholder2
    ? `${element.placeholder};${element.placeholder2}`
    : `${element.placeholder}`;
  element.preview = applyStyles(content, element.linkColor, element.textColor);
};

const updatePicklistOptions = (options) => {
  const element = store.elements[props.selectedIndex];
  if (
    ["SINGLE_OPTIONS", "MULTIPLE_OPTIONS", "RADIO", "CHECKBOX"].includes(
      element?.dataType
    )
  ) {
    // Always update calculatedOptions, even when calculations are disabled
    // This ensures values are preserved when toggling the calculations feature
    element.calculatedOptions = [...options];

    // Update picklistOptions with just the labels for backward compatibility
    element.picklistOptions = options.map((option: any) =>
      typeof option === "object" && option !== null ? option.label : option
    );
  } else {
    element.picklistOptions = options;
  }
  if (element.id) {
    element.customfieldUpdated = true;
  }
  store.anyUnsavedChanges = true;
};

const updateStyle = (style) => {
  const element = store.elements[props.selectedIndex];
  if (element) {
    element.style = style;
    store.anyUnsavedChanges = true;
  }
};

const updateEnableCalculations = (newValue) => {
  // If disabling calculations, save a backup of the current calculation values
  if (!newValue && store.elements[props.selectedIndex].calculatedOptions) {
    // Create a backup of calculation values before disabling
    store.elements[props.selectedIndex]._calculatedOptionsBackup = cloneDeep(
      store.elements[props.selectedIndex].calculatedOptions
    );
  }

  // Update enabled state
  store.elements[props.selectedIndex].enableCalculations = newValue;

  // If enabling calculations, restore from backup or recreate from scratch
  if (newValue) {
    const currentOptions =
      store.elements[props.selectedIndex].picklistOptions || [];

    if (store.elements[props.selectedIndex]._calculatedOptionsBackup) {
      // Create a map of label -> calculatedValue from backup
      const valueMap = {};
      store.elements[props.selectedIndex]._calculatedOptionsBackup.forEach(
        (option) => {
          if (typeof option === "object" && option.label) {
            valueMap[option.label] = option.calculatedValue;
          }
        }
      );

      // Rebuild calculatedOptions from current options, preserving calculation values
      store.elements[props.selectedIndex].calculatedOptions =
        currentOptions.map((option) => {
          const label = typeof option === "object" ? option.label : option;
          const calculatedValue = valueMap[label] || "";
          return { label, calculatedValue };
        });
    } else {
      // No backup available, create fresh ones
      store.elements[props.selectedIndex].calculatedOptions =
        currentOptions.map((option) => {
          const label = typeof option === "object" ? option.label : option;
          return { label, calculatedValue: "" };
        });
    }
  }

  store.anyUnsavedChanges = true;
};

const updateCalculation = (calculation) => {
  store.elements[props.selectedIndex].calculation = calculation;
  store.anyUnsavedChanges = true;
};

const saveOpportunitySettings = (settings) => {
  store.opportunitySettings = settings;
  store.anyUnsavedChanges = true;
  store.showOpportunitiesModal = false;
};

/**
 * Handle contact association settings save
 */
const saveContactAssociationSettings = (settings) => {
  store.contactAssociationSettings = settings;
  store.anyUnsavedChanges = true;
  store.showContactAssociationModal = false;
};

const tncElement = store.elements[props.selectedIndex];
const checkHideCondition = (elementTag) => {
  let showHideFieldsAlert = [];
  store.builderConfig?.conditionalLogic?.forEach((condition: any) => {
    if (condition?.outcome?.type === "showHideFields") {
      const { value } = condition.outcome;
      const fields = Array.isArray(value) ? value : [value];
      showHideFieldsAlert = [...new Set([...showHideFieldsAlert, ...fields])];
    }
  });
  return showHideFieldsAlert.includes(elementTag?.hiddenFieldQueryKey);
};
watch(
  () => [
    tncElement?.placeholder,
    tncElement?.placeholder2,
    tncElement?.mobilePlaceholder,
    tncElement?.mobilePlaceholder2,
    tncElement?.linkColor,
    tncElement?.textColor,
  ],
  () => {
    updatePreview(tncElement);
  }
);

const checkUniqueKeyExists = (key: string, excludeIndex?: number) => {
  // Check if the field is standard
  if (store.elements[props.selectedIndex]?.standard) {
    return false;
  }

  // Check in store.customFields
  const isUniqueKeyExistsInCustomFields = store.customFields.some(
    (customField) => {
      const uniqueKey = customField.fieldKey?.split(".")?.[1];
      return uniqueKey === key || customField.fieldKey === key;
    }
  );

  // Check in store.elements (excluding current element if excludeIndex is provided)
  const isUniqueKeyExistsInElements = store.elements.some((element, index) => {
    if (excludeIndex !== undefined && index === excludeIndex) {
      return false;
    }
    return element.fieldKey === key;
  });

  return isUniqueKeyExistsInCustomFields || isUniqueKeyExistsInElements;
};

const checkUniqueCustomFieldNameExists = (
  name: string,
  excludeIndex?: number
) => {
  // Check if the field is standard
  if (store.elements[props.selectedIndex]?.standard) {
    return false;
  }
  const isCustomFieldNameExistsInCustomFields = store.customFields.some(
    (customField, index) => {
      if (excludeIndex !== undefined && index === excludeIndex) {
        return false;
      }
      return customField.name === name || customField.label === name;
    }
  );
  const isCustomFieldNameExistsInElements = store.elements.some(
    (element, index) => {
      if (excludeIndex !== undefined && index === excludeIndex) {
        return false;
      }
      return element.name === name || element.label === name;
    }
  );
  return (
    isCustomFieldNameExistsInCustomFields || isCustomFieldNameExistsInElements
  );
};

const updateLabel = (value: string) => {
  store.elements[props.selectedIndex].label = value;
  uniqueKeyPlaceholder.value = snakeCase(value);
  if (
    store.elements[props.selectedIndex]?.dataType === "SCORE" &&
    !uniqueKeyPlaceholder.value.startsWith("score_")
  ) {
    uniqueKeyPlaceholder.value = "score_" + uniqueKeyPlaceholder.value;
  }
  if (
    store.elements[props.selectedIndex]?.dataType ===
      MISMATCH_DATA_TYPE.RATING &&
    !uniqueKeyPlaceholder.value.startsWith(
      "rating_" + UNIQUE_KEY_FIELD_KEYS.RATING
    )
  ) {
    uniqueKeyPlaceholder.value =
      "rating_" +
      UNIQUE_KEY_FIELD_KEYS.RATING +
      "_" +
      uniqueKeyPlaceholder.value;
  }
  if (!store.elements[props.selectedIndex]?.id) {
    updateUniqueKey(uniqueKeyPlaceholder.value);
    customFieldError.value = checkUniqueCustomFieldNameExists(
      value,
      props.selectedIndex
    );
  }
  store.anyUnsavedChanges = true;
};

const onCaptchaInvisibleChange = (value: boolean) => {
  store.elements[props.selectedIndex].invisible = value;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};

const handleLabelAlignment = (value: AlignmentPosition) => {
  store.elements[props.selectedIndex].labelAlignment = value;
  store.anyUnsavedChanges = true;
};

const updatePlaceholder = (value: string) => {
  store.elements[props.selectedIndex].placeholder = value;
  store.anyUnsavedChanges = true;
};
const updateShortLabel = (value: string) => {
  store.elements[props.selectedIndex].shortLabel = value;
  store.anyUnsavedChanges = true;
};
const updateHiddenQuery = (value: string) => {
  store.elements[props.selectedIndex].hiddenFieldQueryKey = value;
  store.anyUnsavedChanges = true;
};

const updateUniqueKey = (value: string) => {
  if (value.trim() !== "") {
    store.elements[props.selectedIndex].fieldKey = value;
    store.anyUnsavedChanges = true;
    uniqueKeyError.value = checkUniqueKeyExists(value, props.selectedIndex);
  }
};

const updateRequired = (value: boolean) => {
  store.elements[props.selectedIndex].required = value;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};
const updateHidden = (value: boolean) => {
  store.elements[props.selectedIndex].hidden = value;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};
const updateEnableCountryPicker = (value: boolean) => {
  store.elements[props.selectedIndex].enableCountryPicker = value;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};
const updateHiddenValue = (value: string) => {
  store.elements[props.selectedIndex].hiddenFieldValue = value;
  store.anyUnsavedChanges = true;
};
const updateSource = (value: string) => {
  store.elements[props.selectedIndex].value = value;
  store.anyUnsavedChanges = true;
};
const updateAltImage = (value: string) => {
  store.elements[props.selectedIndex].alt = value;
  store.anyUnsavedChanges = true;
};
const updateDateFormat = (value: string) => {
  // update the placeholder with the new format
  store.elements[props.selectedIndex].placeholder = value;
  store.elements[props.selectedIndex].format = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};
const updateDateSeparator = (value: string) => {
  store.elements[props.selectedIndex].separator = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateDatedisablePicker = (value: boolean) => {
  store.elements[props.selectedIndex].disablePicker = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateSpreadColumns = (value: boolean) => {
  store.elements[props.selectedIndex].spreadColumns = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateNumberOfColumns = (value: number) => {
  store.elements[props.selectedIndex].columnsNumber = value;
};

const updateFieldWidthPercentage = (value: number) => {
  store.elements[props.selectedIndex].fieldWidthPercentage = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const checkInitialValidation = () => {
  if (!store.elements[props.selectedIndex]?.id) {
    const currentFieldKey = store.elements[props.selectedIndex]?.fieldKey;

    if (currentFieldKey) {
      // Check if fieldKey exists in other elements
      uniqueKeyError.value = currentFieldKey
        ? checkUniqueKeyExists(currentFieldKey, props.selectedIndex)
        : false;
    }

    const currentCustomFieldName =
      store.elements[props.selectedIndex]?.name ||
      store.elements[props.selectedIndex]?.label;
    customFieldError.value = checkUniqueCustomFieldNameExists(
      currentCustomFieldName,
      props.selectedIndex
    );
  }
};

onMounted(() => {
  checkInitialValidation();
});

// Sidebar state management via global event bus
const getSidebarState = (
  callback: (state: Record<string, boolean>) => void
): void => {
  if (typeof callback === "function") {
    callback({
      formCustomSidebarOpen: store.isAnyElementActive && !!store.formId,
    });
  }
};

/**
 * Direct getter for this sidebar's open state.
 */
const getFormCustomSidebarState = (
  callback: (state: boolean) => void
): void => {
  if (typeof callback === "function") {
    callback(store.isAnyElementActive && !!store.formId);
  }
};

/**
 * Close the Form Custom Sidebar (and any others) when a global close event is received.
 */
const closeAllSidebars = (): void => {
  if (store.isAnyElementActive) {
    store.isAnyElementActive = false;
  }
};

/**
 * Restore sidebar state from a saved snapshot.
 */
const restoreSidebarState = (state: Record<string, boolean>): void => {
  if (state && typeof state.formCustomSidebarOpen === "boolean") {
    store.isAnyElementActive = state.formCustomSidebarOpen;
  }
};

// -----------------------------------------------------------------------------
// Sidebar Event Lifecycle Hooks
// -----------------------------------------------------------------------------

onBeforeMount(() => {
  emitter.on("get-sidebar-state", getSidebarState);
  emitter.on("get-form-custom-sidebar-state", getFormCustomSidebarState);
  emitter.on("close-all-sidebars", closeAllSidebars);
  emitter.on("restore-sidebar-state", restoreSidebarState);
});

onBeforeUnmount(() => {
  emitter.off("get-sidebar-state", getSidebarState);
  emitter.off("get-form-custom-sidebar-state", getFormCustomSidebarState);
  emitter.off("close-all-sidebars", closeAllSidebars);
  emitter.off("restore-sidebar-state", restoreSidebarState);
});

const updatePlaceholder2 = (value: string): void => {
  store.elements[props.selectedIndex].placeholder2 = value;
  store.anyUnsavedChanges = true;
};

const handleRatingConfig = (key, value) => {
  store.elements[props.selectedIndex][key] = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

// Default content style for T&C label
const tncLabelContentStyle = computed(() => {
  let color = TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR;
  if (isMobile.value && store.fieldStyle?.mobileLabelColor) {
    color = "#" + String(store.fieldStyle?.mobileLabelColor);
  } else if (store.fieldStyle?.labelColor) {
    color = "#" + String(store.fieldStyle?.labelColor);
  }

  return { color };
});

const addSecondTnc = () => {
  if (!store.elements[props.selectedIndex].placeholder2) {
    store.elements[
      props.selectedIndex
    ].placeholder2 = `<p style='font-family: Roboto; font-size: 14px; font-weight: 400; color: ${TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR};'></p>`;
    store.anyUnsavedChanges = true;
  }
};

const removeSecondTnc = () => {
  if (store.elements[props.selectedIndex].placeholder2) {
    store.elements[props.selectedIndex].placeholder2 = undefined;
    store.anyUnsavedChanges = true;
  }
};

// URL for custom field tooltip learn more link (same for all field types)
const customFieldNameTooltipLearnMoreUrl = CUSTOM_FIELD_NAME_TOOLTIP_LEARN_MORE_URL;

// Computed properties for custom field name tooltip
const customFieldNameTooltipKey = computed(() => {
  const model = store.elements[props.selectedIndex]?.model;
  return getCustomFieldNameTooltipKey(model);
});

const customFieldNameTooltipLearnMoreKey = computed(() => {
  const model = store.elements[props.selectedIndex]?.model;
  return getCustomFieldNameTooltipLearnMoreKey(model);
});

// Wrapper functions for template access to utilities
const isCustomObjectFieldWrapper = (model: string) =>
  isCustomObjectField(model);
const isTrueCustomObjectFieldWrapper = (model: string) =>
  isTrueCustomObjectField(model);
const getCustomObjectNameWrapper = (model: string) =>
  getCustomObjectName(model, store.customObjects || []);
const getCustomObjectNamePluralWrapper = (model: string) =>
  getCustomObjectNamePlural(model, store.customObjects || []);
</script>

<template>
  <div>
    <div class="relative mb-6 flex items-center justify-center">
      <div class="arrow-button absolute left-0" @click="onBack">
        <ArrowLeftIcon class="mr-2 h-4 w-4 text-gray-800" />
      </div>
      <div class="text-md text-gray-900">{{ getTypeLabel }}</div>
    </div>
    <div
      :class="
        [
          'TEXTBOX_LIST',
          'SINGLE_OPTIONS',
          'MULTIPLE_OPTIONS',
          'RADIO',
          'CHECKBOX',
          'FILE_UPLOAD',
          'SCORE',
        ].includes(store.elements[selectedIndex]?.dataType) &&
        !store.elements[selectedIndex]?.picklistOptionsImage?.length
          ? ''
          : 'hide-outer-tabs'
      "
    >
      <UITabs
        justify-content="space-around"
        default-value="Content"
        type="line"
      >
        <UITabPane name="Content" :tab="t('common.content')">
          <AddressSettings
            v-if="store.elements[selectedIndex]?.tag === 'group_address'"
            :address="store.elements[selectedIndex]"
            :selected-index="selectedIndex"
          ></AddressSettings>
          <template v-else-if="store.elements[selectedIndex]?.tag === 'button'">
            <div class="button-sidebar-wrapper">
              <ButtonSidebar
                ref="buttonSidebarRef"
                :property="cloneDeep(store.elements[props.selectedIndex])"
                @update:button="handleButtonUpdate"
              />
            </div>
          </template>
          <template v-else-if="store.elements[selectedIndex]?.tag === 'payment'">
            <PaymentSettings
              :payment="store.elements[selectedIndex]"
              :selected-index="selectedIndex"
            />
          </template>
          <template v-else>
            <UICollapse
              id="custom-field-settings"
              :arrow-placement="'left'"
              :default-expanded-names="[t('common.generalSettings')]"
            >
              <RatingEditor
                v-if="store.elements[selectedIndex]?.type === 'rating'"
                :rating-element="store.elements[selectedIndex]"
                :index="selectedIndex"
                :product="store.product"
                :is-required-field-disable="isRequiredCheckboxDisabled"
                :is-hidden-field-disable="isHiddenFieldDisable"
                :get-field-width-percentage="
                  () => (getFieldWidthPercentage(), 0)
                "
                :is-form="true"
                @update-label="updateLabel"
                @update-short-label="updateShortLabel"
                @update-field-width-percentage="updateFieldWidthPercentage"
                @update-required="updateRequired"
                @update-rating-count="handleRatingConfig('count', $event)"
                @update-icon-selected-state="
                  handleRatingConfig('color', $event)
                "
                @update-lowest-rating="
                  handleRatingConfig('lowestRating', $event)
                "
                @update-highest-rating="
                  handleRatingConfig('highestRating', $event)
                "
                @update-icon-selected="handleRatingConfig('iconType', $event)"
                @update-icon-alignment="
                  handleRatingConfig('iconAlignment', $event)
                "
                @update-calculation-type="
                  handleRatingConfig('calculationType', $event)
                "
                @update-icon-unselected-state="
                  handleRatingConfig('inactiveColor', $event)
                "
                @update-label-alignment="handleLabelAlignment"
              ></RatingEditor>
              <UICollapseItem
                v-else
                id="general-settings"
                :title="t('common.generalSettings')"
                :name="t('common.generalSettings')"
              >
                <template #header>
                  <div class="flex w-full items-center justify-between">
                    <div>{{ t("common.generalSettings") }}</div>
                    <div
                      v-if="
                        ['header', 'terms_and_conditions'].includes(
                          store.elements[selectedIndex]?.tag
                        )
                      "
                      @click.stop
                    >
                      <UIDropdown
                        id="general-device-toggle"
                        :options="deviceOptions"
                        @select="switchDeviceMode"
                      >
                        <template #default>
                          <div class="flex items-center gap-0.5">
                            <Phone01Icon
                              v-if="store.deviceMode === 'mobile'"
                              class="h-4 w-4 cursor-pointer"
                            />
                            <Monitor04Icon
                              v-else
                              class="h-4 w-4 cursor-pointer"
                            />
                            <ChevronDownIcon class="h-3 w-3" />
                          </div>
                        </template>
                      </UIDropdown>
                    </div>
                  </div>
                </template>
                <div v-if="store.elements[selectedIndex]?.type == 'html'">
                  <div class="field-name-sidebar">
                    <UIInput
                      id="htmlLabelInline"
                      class="no-border w-full bg-transparent p-0 text-base font-semibold shadow-none outline-none"
                      :model-value="getLabel"
                      :status="customFieldError ? 'error' : undefined"
                      @update:model-value="updateLabel"
                      @blur="store.addToHistory()"
                    />
                    <div
                      v-if="customFieldError"
                      class="mt-1 text-xs text-red-500"
                    >
                      {{ t("common.customFieldExistsError") }}
                    </div>
                  </div>
                  <div class="form-group">
                    <Card :header="fieldPlaceholder">
                      <template #default>
                        <UIButton
                          id="editHtml"
                          type="default"
                          class="w-[100%]"
                          @click="onClickOfEditHTML"
                          >{{ t("common.editHTML") }}</UIButton
                        >
                      </template>
                    </Card>
                  </div>
                  <div>
                    <CodeEditorModel
                      v-if="showModal"
                      :model-active="showModal"
                      :code="code"
                      @update:show-model="showModelUpdate"
                      @update:submit="submitUpdate"
                    />
                  </div>
                </div>
                <div
                  v-else-if="store.elements[selectedIndex]?.tag === 'header'"
                >
                  <TextSidebar
                    :property="
                      JSON.parse(
                        JSON.stringify(store.elements[props.selectedIndex])
                      )
                    "
                    @update:text="handleTextUpdate"
                  />
                </div>
                <div
                  v-else-if="store.elements[selectedIndex]?.tag === 'source'"
                >
                  <h6 class="field-name-sidebar">
                    {{ store.elements[selectedIndex]?.label }}
                  </h6>
                  <div class="form-group">
                    <UIInput
                      id="sourceLabel"
                      :placeholder="t('common.enterSourceValue')"
                      class="standard-input-ghl"
                      :model-value="getSource"
                      @update:model-value="updateSource"
                      @blur="store.addToHistory()"
                    ></UIInput>
                  </div>
                </div>
                <div
                  v-else-if="store.elements[selectedIndex]?.tag === 'captcha'"
                >
                  <h6 class="field-name-sidebar">
                    {{ t("fields.customized.botProtection") }}
                  </h6>
                  <div class="form-group">
                    <div class="flex space-x-3">
                      <span
                        class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                        >{{ t("common.label") }}</span
                      >
                    </div>
                    <UIInput
                      id="captchaLabel"
                      class="standard-input-ghl"
                      :model-value="getLabel"
                      @update:model-value="updateLabel"
                      @blur="store.addToHistory()"
                    >
                    </UIInput>
                  </div>
                  <div class="form-group mt-4">
                    <LabelAlignment
                      :label-alignment="
                        store.elements[selectedIndex]?.labelAlignment
                      "
                      :is-element-level="true"
                      @update:label-alignment="handleLabelAlignment"
                    ></LabelAlignment>
                  </div>
                  <div class="form-group">
                    <CaptchaVerificationMode
                      :invisible="!!store.elements[selectedIndex]?.invisible"
                      @update:invisible="onCaptchaInvisibleChange"
                    />
                  </div>
                </div>
                <div v-else-if="store.elements[selectedIndex]?.tag === 'image'">
                  <div class="field-name-sidebar">
                    <UIInput
                      id="imageLabelInline"
                      class="no-border w-full bg-transparent p-0 text-base font-semibold shadow-none outline-none"
                      :model-value="getLabel"
                      :status="customFieldError ? 'error' : undefined"
                      @update:model-value="updateLabel"
                      @blur="store.addToHistory()"
                    />
                    <div
                      v-if="customFieldError"
                      class="mt-1 text-xs text-red-500"
                    >
                      {{ t("common.customFieldExistsError") }}
                    </div>
                  </div>

                  <MediaCenter
                    v-model:value="store.elements[props.selectedIndex].url"
                    :sub-category="MediaCenterSubCategory.FORM"
                  />

                  <div class="form-group">
                    <div class="flex space-x-3">
                      <span
                        class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                        >{{ t("common.altText") }}</span
                      >
                    </div>
                    <UIInput
                      id="altText"
                      class="standard-input-ghl"
                      :model-value="getAltImage"
                      @update:model-value="updateAltImage"
                      @blur="store.addToHistory()"
                    ></UIInput>
                  </div>
                  <div class="form-group">
                    <div class="flex space-x-3">
                      <div class="alignment">
                        <div class="alignment-div">
                          {{ t("common.alignment") }}
                        </div>
                        <div class="icons-collection">
                          <AlignLeftIcon
                            class="custom-icons h-5 w-5"
                            @click="alignImage('left')"
                          ></AlignLeftIcon>
                          <AlignCenterIcon
                            class="custom-icons h-5 w-5"
                            @click="alignImage('center')"
                          ></AlignCenterIcon>
                          <AlignRightIcon
                            class="custom-icons h-5 w-5"
                            @click="alignImage('right')"
                          ></AlignRightIcon>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="flex space-x-3">
                      <CountInput
                        :label="t('common.imageWidth')"
                        :value="store.elements[selectedIndex]?.width || ''"
                        :max="1800"
                        @update:value="handleImageWidth"
                      />
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="flex space-x-3">
                      <CountInput
                        :label="t('common.imageHeight')"
                        :value="store.elements[selectedIndex]?.height || ''"
                        :max="1800"
                        @update:value="handleImageHeight"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="
                    store.elements[selectedIndex]?.tag ===
                    'terms_and_conditions'
                  "
                >
                  <h6 class="field-name-sidebar">
                    {{
                      store.elements[selectedIndex]?.label ||
                      store.elements[selectedIndex]?.name
                    }}
                  </h6>
                  <div class="form-group flex flex-col gap-y-4">
                    <div>
                      <span
                        class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                        >{{ t("common.tnc1") }}</span
                      >
                      <div>
                        <!-- First TNC Editor -->
                        <div class="mb-4">
                          <RichTextEditor
                            :is-submit-message="true"
                            :content-style="tncLabelContentStyle"
                            :model-value="
                              getDeviceAware(
                                store.elements[selectedIndex],
                                'placeholder',
                                'mobilePlaceholder',
                                isMobile
                              )
                            "
                            @update:model-value="
                              (value) => {
                                setDeviceAware(
                                  store.elements[selectedIndex],
                                  'placeholder',
                                  'mobilePlaceholder',
                                  value,
                                  isMobile
                                )
                                store.anyUnsavedChanges = true
                              }
                            "
                          />
                        </div>

                        <!-- Add/Remove TNC buttons -->
                        <div class="mb-4 flex justify-end space-x-2">
                          <UIButton
                            v-if="!store.elements[selectedIndex]?.placeholder2"
                            id="add-second-tnc"
                            type="default"
                            size="small"
                            @click="addSecondTnc"
                          >
                            <PlusIcon class="mr-1.5 h-5 w-5" />
                            {{ t("common.addAnotherTnc") }}
                          </UIButton>
                        </div>

                        <!-- Second TNC Editor -->
                        <div
                          v-if="store.elements[selectedIndex]?.placeholder2"
                          class="mb-1"
                        >
                          <div class="flex items-center justify-between">
                            <span
                              class="hl-text-input-label block text-sm font-medium text-gray-700"
                            >
                              {{ t("common.tnc2") }}
                            </span>
                            <UIButton
                              id="remove-second-tnc-inline"
                              type="danger"
                              size="small"
                              @click="removeSecondTnc"
                            >
                              <Trash01Icon class="h-5 w-5" />
                            </UIButton>
                          </div>
                        </div>
                        <div
                          v-if="store.elements[selectedIndex]?.placeholder2"
                          class="mb-4"
                        >
                          <RichTextEditor
                            :is-submit-message="true"
                            :content-style="tncLabelContentStyle"
                            :model-value="
                              getDeviceAware(
                                store.elements[selectedIndex],
                                'placeholder2',
                                'mobilePlaceholder2',
                                isMobile
                              )
                            "
                            @update:model-value="
                              (value) => {
                                setDeviceAware(
                                  store.elements[selectedIndex],
                                  'placeholder2',
                                  'mobilePlaceholder2',
                                  value,
                                  isMobile
                                )
                                store.anyUnsavedChanges = true
                              }
                            "
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div class="flex space-x-3">
                        <div
                          class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                        >
                          {{ t("common.queryKey") }}
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <InfoCircleIcon
                                class="h-4 w-4 text-gray-400"
                              ></InfoCircleIcon>
                            </template>
                            {{ t("common.querykeyTooltipMessage") }}
                          </UITooltip>
                        </div>
                      </div>
                      <div>
                        <UIInput
                          id="fieldQueryKey"
                          class="standard-input-ghl"
                          :model-value="getHiddenQuery"
                          @update:model-value="updateHiddenQuery"
                          @blur="store.addToHistory()"
                        ></UIInput>
                      </div>
                    </div>
                    <div class="form-check-req-hid">
                      <UICheckbox
                        id="fieldRequired"
                        :disabled="isRequiredCheckboxDisabled"
                        :checked="getRequired"
                        @update:checked="updateRequired"
                        >{{ t("common.required") }}</UICheckbox
                      >
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="store.elements[selectedIndex]?.tag === 'payment'"
                >
                  <PaymentSettings
                    :payment="store.elements[selectedIndex]"
                    :selected-index="selectedIndex"
                  ></PaymentSettings>
                </div>
                <div
                  v-else-if="
                    ['date'].includes(store.elements[selectedIndex]?.type)
                  "
                >
                  <DateSettings
                    :date-element="store.elements[selectedIndex]"
                    :is-required-field-disable="isRequiredCheckboxDisabled"
                    :is-hidden-field-disable="isHiddenFieldDisable"
                    :get-field-width-percentage="
                      () => getFieldWidthPercentage()
                    "
                    :is-form="true"
                    :is-custom-field-error="customFieldError"
                    @update-label="updateLabel"
                    @update-placeholder="updatePlaceholder"
                    @update-short-label="updateShortLabel"
                    @update-hidden-query="updateHiddenQuery"
                    @update-field-width-percentage="updateFieldWidthPercentage"
                    @update-required="updateRequired"
                    @update-hidden="updateHidden"
                    @update-hidden-value="updateHiddenValue"
                    @update-date-format="updateDateFormat"
                    @update-date-separator="updateDateSeparator"
                    @update-date-disable-picker="updateDatedisablePicker"
                    @update-label-alignment="handleLabelAlignment"
                  ></DateSettings>
                  <div
                    v-if="
                      store.elements[selectedIndex]?.model === 'opportunity'
                    "
                  >
                    <UIDivider />
                    <div class="flex justify-center">
                      <UIButton
                        id="edit-opportunity-settings"
                        type="tertiary"
                        @click="store.showOpportunitiesModal = true"
                      >
                        <Edit01Icon class="mr-2 h-4 w-4" />
                        {{ t("common.editOpportunitySettings") }}
                      </UIButton>
                    </div>
                    <p v-if="!store.opportunitySettings" class="pt-6">
                      <UIAlert id="warning-message" type="warning">
                        <template #content>
                          {{ t("common.opportunitySettingsWarning") }}
                        </template>
                      </UIAlert>
                    </p>
                  </div>
                  <div
                    v-if="
                      isTrueCustomObjectFieldWrapper(
                        store.elements[selectedIndex]?.model
                      )
                    "
                  >
                    <UIDivider />
                    <div class="flex justify-center">
                      <UIButton
                        id="edit-custom-object-association"
                        type="tertiary"
                        @click="
                          store.currentCustomObjectModel =
                            store.elements[selectedIndex]?.model;
                          store.showContactAssociationModal = true;
                        "
                      >
                        <Edit01Icon class="mr-2 h-4 w-4" />
                        {{
                          t("common.editAssociationButton", {
                            edit: t("common.edit"),
                            objectName: getCustomObjectNamePluralWrapper(
                              store.elements[selectedIndex]?.model
                            ),
                            association: t("common.association"),
                          })
                        }}
                      </UIButton>
                    </div>
                  </div>
                  <EditOpportunitySettingsModal
                    :show="store.showOpportunitiesModal"
                    @update:success="saveOpportunitySettings"
                    @update:cancel="store.showOpportunitiesModal = false"
                  />
                </div>
                <div v-else>
                  <div class="form-group">
                    <div class="flex space-x-3">
                      <span
                        class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                        >{{ t("common.label") }}</span
                      >
                    </div>
                    <UIInput
                      id="label"
                      class="standard-input-ghl"
                      :model-value="getLabel"
                      :status="customFieldError ? 'error' : undefined"
                      @update:model-value="updateLabel"
                      @blur="store.addToHistory()"
                    ></UIInput>
                    <div
                      v-if="customFieldError"
                      class="mt-1 text-xs text-red-500"
                    >
                      {{ t("common.customFieldExistsError") }}
                    </div>
                  </div>
                  <div class="form-group">
                    <LabelAlignment
                      :label-alignment="
                        store.elements[selectedIndex]?.labelAlignment
                      "
                      :is-element-level="true"
                      @update:label-alignment="handleLabelAlignment"
                    ></LabelAlignment>
                  </div>
                  <div
                    v-if="
                      !['TEXTBOX_LIST'].includes(
                        store.elements[selectedIndex]?.dataType
                      )
                    "
                    class="form-group"
                  >
                    <div class="flex space-x-3">
                      <span
                        class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                        >{{ t("common.placeholder") }}</span
                      >
                    </div>
                    <UIInput
                      id="fieldPlaceholder"
                      class="standard-input-ghl"
                      :model-value="getPlaceholder"
                      @update:model-value="updatePlaceholder"
                      @blur="store.addToHistory()"
                    ></UIInput>
                  </div>
                  <div
                    v-if="
                      ![
                        'image',
                        'header',
                        'button',
                        'terms_and_conditions',
                      ].includes(store.elements[selectedIndex]?.tag)
                    "
                    class="form-group"
                  >
                    <div class="flex space-x-3">
                      <span
                        class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                        >{{ t("common.shortLabel") }}</span
                      >
                    </div>
                    <UIInput
                      id="shortLabel"
                      class="standard-input-ghl"
                      :model-value="getShortLabel"
                      @update:model-value="updateShortLabel"
                      @blur="store.addToHistory()"
                    >
                    </UIInput>
                  </div>
                  <div
                    v-if="
                      ![
                        'TEXTBOX_LIST',
                        'MULTIPLE_OPTIONS',
                        'FILE_UPLOAD',
                        'SIGNATURE',
                      ].includes(store.elements[selectedIndex]?.dataType)
                    "
                    class="form-group"
                  >
                    <div class="flex space-x-3">
                      <div
                        class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                      >
                        {{ t("common.queryKey") }}
                        <UITooltip trigger="hover" placement="top">
                          <template #trigger>
                            <InfoCircleIcon
                              class="h-4 w-4 text-gray-400"
                            ></InfoCircleIcon>
                          </template>
                          {{ t("common.querykeyTooltipMessage") }}
                        </UITooltip>
                      </div>
                    </div>
                    <UIInput
                      id="fieldQueryKey"
                      class="standard-input-ghl"
                      :model-value="getHiddenQuery"
                      @update:model-value="updateHiddenQuery"
                      @blur="store.addToHistory()"
                    ></UIInput>
                  </div>

                  <div
                    v-if="!fieldFullWidth(store.elements[selectedIndex]?.type)"
                    class="form-group"
                  >
                    <div class="flex justify-between pb-2">
                      <div
                        class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                      >
                        {{ t("common.style.fieldWidth") }}
                      </div>
                    </div>
                    <UIInputGroup>
                      <UIInputNumber
                        id="fieldWidthKey"
                        size="large"
                        class="w-100"
                        :min="1"
                        :max="100"
                        :show-button="false"
                        :model-value="
                          store.elements[props.selectedIndex]
                            ?.fieldWidthPercentage
                        "
                        @update:model-value="updateFieldWidthPercentage"
                      ></UIInputNumber
                      ><UIInputGroupLabel>%</UIInputGroupLabel>
                    </UIInputGroup>
                  </div>
                  <div
                    v-if="
                      ['RADIO', 'CHECKBOX'].includes(
                        store.elements[selectedIndex]?.dataType
                      ) &&
                      store.elements[selectedIndex]?.picklistOptionsImage
                        ?.length
                    "
                    class="form-group mt-4"
                  >
                    <div class="flex justify-between pb-2">
                      <div
                        class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                      >
                        {{ t("common.spreadToColumns") }}
                      </div>
                      <UISwitch
                        :value="getSpreadColumns"
                        @update:value="updateSpreadColumns"
                      ></UISwitch>
                    </div>

                    <UIInputGroup v-if="getSpreadColumns">
                      <UIInputNumber
                        id="fieldQueryKey"
                        size="large"
                        class="w-100"
                        :min="1"
                        :max="10"
                        :default-value="getDefaultNumberOfColumns"
                        :show-button="false"
                        :model-value="getNumberOfColumns"
                        @update:model-value="updateNumberOfColumns"
                        @blur="() => store.addToHistory()"
                      ></UIInputNumber
                      ><UIInputGroupLabel>{{
                        t("common.columns")
                      }}</UIInputGroupLabel>
                    </UIInputGroup>
                  </div>
                  <div class="form-check-req-hid">
                    <UICheckbox
                      id="fieldRequired"
                      :disabled="isRequiredCheckboxDisabled"
                      :checked="getRequired"
                      @update:checked="updateRequired"
                      >{{ t("common.required") }}</UICheckbox
                    >
                    <UICheckbox
                      v-if="
                        ![
                          'TEXTBOX_LIST',
                          'SINGLE_OPTIONS',
                          'MULTIPLE_OPTIONS',
                          'FILE_UPLOAD',
                          'SIGNATURE',
                        ].includes(store.elements[selectedIndex]?.dataType)
                      "
                      id="fieldCheckbox"
                      :disabled="isHiddenFieldDisable"
                      :checked="getHidden"
                      @update:checked="updateHidden"
                      >{{ t("common.hidden") }}</UICheckbox
                    >
                  </div>
                  <div
                    v-if="store.elements[selectedIndex]?.hidden"
                    class="form-group hidden-field"
                  >
                    <div class="flex space-x-3">
                      <span
                        class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                        >{{ t("common.hiddenValue") }}</span
                      >
                    </div>
                    <UIInput
                      id="fieldHiddenvalue"
                      class="standard-input-ghl"
                      :model-value="getHiddenValue"
                      @update:model-value="updateHiddenValue"
                      @blur="store.addToHistory()"
                    ></UIInput>
                  </div>
                  <div>
                    <UICheckbox
                      v-if="
                        store.elements[selectedIndex]?.tag == 'phone' ||
                        store.elements[selectedIndex]?.dataType == 'PHONE'
                      "
                      id="fieldCheckbox"
                      :checked="getEnableCountryPicker"
                      @update:checked="updateEnableCountryPicker"
                      >{{ t("common.enableCountryPicker") }}</UICheckbox
                    >
                  </div>
                  <div
                    v-if="
                      store.elements[selectedIndex]?.model === 'opportunity'
                    "
                  >
                    <UIDivider />
                    <div class="flex justify-center">
                      <UIButton
                        id="edit-opportunity-settings"
                        type="tertiary"
                        @click="store.showOpportunitiesModal = true"
                      >
                        <Edit01Icon class="mr-2 h-4 w-4" />
                        {{ t("common.editOpportunitySettings") }}
                      </UIButton>
                    </div>
                    <p v-if="!store.opportunitySettings" class="pt-6">
                      <UIAlert id="warning-message" type="warning">
                        <template #content>
                          {{ t("common.opportunitySettingsWarning") }}
                        </template>
                      </UIAlert>
                    </p>
                  </div>
                  <div
                    v-if="
                      isTrueCustomObjectFieldWrapper(
                        store.elements[selectedIndex]?.model
                      )
                    "
                  >
                    <UIDivider />
                    <div class="flex justify-center">
                      <UIButton
                        id="edit-custom-object-association"
                        type="tertiary"
                        @click="
                          store.currentCustomObjectModel =
                            store.elements[selectedIndex]?.model;
                          store.showContactAssociationModal = true;
                        "
                      >
                        <Edit01Icon class="mr-2 h-4 w-4" />
                        {{
                          t("common.editAssociationButton", {
                            edit: t("common.edit"),
                            objectName: getCustomObjectNamePluralWrapper(
                              store.elements[selectedIndex]?.model
                            ),
                            association: t("common.association"),
                          })
                        }}
                      </UIButton>
                    </div>
                  </div>
                  <EditOpportunitySettingsModal
                    :show="store.showOpportunitiesModal"
                    @update:success="saveOpportunitySettings"
                    @update:cancel="store.showOpportunitiesModal = false"
                  />
                  <ContactAssociationSettingsModal
                    :show="store.showContactAssociationModal"
                    :object-model="store.currentCustomObjectModel"
                    @update:success="saveContactAssociationSettings"
                    @update:cancel="store.showContactAssociationModal = false"
                  />
                  <p
                    v-if="checkHideCondition(store.elements[selectedIndex])"
                    class="mt-3"
                  >
                    <UIAlert id="warning-message" type="warning">
                      <template #content>
                        {{
                          t("common.conditionalLogicAlertOnShowHide")
                        }}</template
                      >
                    </UIAlert>
                  </p>
                </div>
                <div
                  v-if="
                    store.elements[selectedIndex]?.tag === 'email' &&
                    !store.elements[selectedIndex]?.hidden
                  "
                >
                  <div class="mt-8 flex justify-between">
                    <span class="hl-text-input-label mb-1 block text-gray-700">
                      {{ t("common.validateEmailHeader") }}
                    </span>
                    <UICheckbox
                      id="email-checkbox"
                      v-model:checked="enableValidateEmailModel"
                      @update:checked="store.addToHistory"
                    />
                  </div>
                  <div class="text-xs text-gray-700">
                    {{ t("common.validateEmailDescription") }}
                  </div>
                </div>
                <div
                  v-if="
                    (store.elements[selectedIndex]?.tag === 'phone' ||
                      store.elements[selectedIndex]?.dataType === 'PHONE') &&
                    !store.elements[selectedIndex]?.hidden
                  "
                >
                  <div class="mt-8 flex justify-between">
                    <span class="hl-text-input-label mb-1 block text-gray-700">
                      {{ t("common.validatePhoneHeader") }}
                    </span>
                    <UICheckbox
                      id="phone-checkbox"
                      v-model:checked="enableValidatePhoneModel"
                      @update:checked="store.addToHistory"
                    />
                  </div>
                  <div class="mb-4 text-xs text-gray-700">
                    {{ t("common.validatePhoneDescription") }}
                  </div>
                  <UIAlert id="warning-message" type="warning">
                    <template #content>
                      {{ t("common.validatePhoneWarning") }}
                    </template>
                  </UIAlert>
                </div>
              </UICollapseItem>
              <UICollapseItem
                v-if="!store.elements[selectedIndex]?.standard"
                id="advanced-settings"
                :title="t('common.advancedSettings')"
                name="Advanced Settings"
              >
                <div>
                  <div
                    class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                  >
                    {{ t("common.customFieldName") }}
                    <UITooltip trigger="hover" placement="top">
                      <template #trigger>
                        <InfoCircleIcon
                          class="h-4 w-4 text-gray-400"
                        ></InfoCircleIcon>
                      </template>
                      <template #default>
                        <span>
                          {{ t(customFieldNameTooltipKey) }}
                          <a
                            :href="customFieldNameTooltipLearnMoreUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-500 underline hover:text-blue-700"
                          >
                            {{ t(customFieldNameTooltipLearnMoreKey) }}
                          </a>
                        </span>
                      </template>
                    </UITooltip>
                  </div>
                  <UIInput
                    id="custom-field-label"
                    class="standard-input-ghl"
                    :model-value="
                      store.elements[props.selectedIndex]?.customFieldLabel ||
                      store.elements[props.selectedIndex]?.name ||
                      store.elements[props.selectedIndex]?.label
                    "
                    :disabled="
                      store.elements[selectedIndex]?.customEdited ||
                      !store.elements[selectedIndex]?.custom
                    "
                    :status="customFieldError ? 'error' : undefined"
                    @update:model-value="updateLabel"
                    @blur="store.addToHistory()"
                  />
                  <div
                    v-if="customFieldError"
                    class="mt-1 text-xs text-red-500"
                  >
                    {{ t("common.customFieldExistsError") }}
                  </div>
                  <div
                    class="switch-handle mb-1 mt-6 block text-sm font-medium text-gray-700"
                  >
                    {{ t("common.uniqueKey") }}
                    <UITooltip trigger="hover" placement="top">
                      <template #trigger>
                        <InfoCircleIcon
                          class="h-4 w-4 text-gray-400"
                        ></InfoCircleIcon>
                      </template>
                      <template #default>
                        <span>
                          {{ t("common.uniqueKeyTooltip") }}
                          <a
                            :href="customFieldNameTooltipLearnMoreUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="ml-1 text-blue-500 underline hover:text-blue-700"
                          >
                            {{ t("common.uniqueKeyTooltipLearnMore") }}
                          </a>
                        </span>
                      </template>
                    </UITooltip>
                  </div>
                  <UIInput
                    id="uniqueKey"
                    class="standard-input-ghl"
                    :model-value="getUniqueKey"
                    :disabled="disableUniqueKey"
                    :placeholder="uniqueKeyPlaceholder"
                    :status="uniqueKeyError ? 'error' : undefined"
                    @update:model-value="updateUniqueKey"
                    @blur="store.addToHistory()"
                  />
                  <div v-if="uniqueKeyError" class="mt-1 text-xs text-red-500">
                    {{ t("common.uniqueKeyNotUniqueError") }}
                  </div>
                </div>
              </UICollapseItem>
            </UICollapse>
          </template>
        </UITabPane>
        <UITabPane
          name="Options"
          :tab="
            store.elements[selectedIndex]?.dataType === 'FILE_UPLOAD'
              ? t('common.uploadOptions')
              : store.elements[selectedIndex]?.dataType === 'SCORE'
              ? t('common.calculations')
              : t('common.options')
          "
          ><ListEditor
            v-if="store.elements[selectedIndex]?.dataType === 'TEXTBOX_LIST'"
            :key="`list-editor-${selectedIndex}-${
              store.elements[props.selectedIndex]?.picklistOptions?.length || 0
            }`"
            :picklist-options="
              store.elements[props.selectedIndex]?.picklistOptions
            "
            :custom-field-created="
              store.elements[selectedIndex]?.customEdited ||
              !store.elements[selectedIndex]?.custom
            "
            :hide-prefill-value="
              isCustomObjectFieldWrapper(store.elements[selectedIndex]?.model)
            "
            @update:picklist-options="updatePicklistOptions"
          />
          <DropdownEditor
            v-if="
              ['SINGLE_OPTIONS', 'MULTIPLE_OPTIONS', 'CHECKBOX'].includes(
                store.elements[selectedIndex]?.dataType
              )
            "
            :key="`dropdown-editor-${selectedIndex}-${
              store.elements[props.selectedIndex]?.enableCalculations
                ? 'calc'
                : 'normal'
            }-${
              store.elements[props.selectedIndex]?.picklistOptions?.length || 0
            }`"
            :selected-element="store.elements[selectedIndex]"
            :options="
              store.elements[props.selectedIndex]?.enableCalculations
                ? store.elements[props.selectedIndex]?.calculatedOptions || []
                : store.elements[props.selectedIndex]?.picklistOptions
            "
            :enable-calculations="
              store.elements[props.selectedIndex]?.enableCalculations
            "
            :hide-prefill-value="
              isCustomObjectFieldWrapper(store.elements[selectedIndex]?.model)
            "
            :custom-field-created="
              store.elements[selectedIndex]?.customEdited ||
              !store.elements[selectedIndex]?.custom
            "
            @update:options="updatePicklistOptions"
            @update:style="updateStyle"
          />
          <RadioSelectEditor
            v-if="store.elements[selectedIndex]?.dataType === 'RADIO'"
            :key="`radio-editor-${selectedIndex}-${
              store.elements[props.selectedIndex]?.enableCalculations
                ? 'calc'
                : 'normal'
            }-${
              store.elements[props.selectedIndex]?.picklistOptions?.length || 0
            }`"
            :selected-element="store.elements[selectedIndex]"
            :options="
              store.elements[props.selectedIndex]?.enableCalculations
                ? store.elements[props.selectedIndex]?.calculatedOptions || []
                : store.elements[props.selectedIndex]?.picklistOptions
            "
            :enable-calculations="
              store.elements[props.selectedIndex]?.enableCalculations
            "
            :hide-prefill-value="
              isCustomObjectFieldWrapper(store.elements[selectedIndex]?.model)
            "
            :custom-field-created="
              store.elements[selectedIndex]?.customEdited ||
              !store.elements[selectedIndex]?.custom
            "
            @update:options="updatePicklistOptions"
            @update:style="updateStyle"
          />
          <FileUploadEditor
            v-if="store.elements[selectedIndex]?.dataType === 'FILE_UPLOAD'"
            :options="
              store.elements[props.selectedIndex]?.picklistOptions || ['all']
            "
            @update:options="updatePicklistOptions"
          />
          <ScoreEditor
            v-if="store.elements[selectedIndex]?.dataType === 'SCORE'"
            :calculation="store.elements[props.selectedIndex]?.calculation"
            @update:calculation="updateCalculation"
          />
          <div v-if="store.elements[selectedIndex]?.dataType === 'FILE_UPLOAD'">
            <div class="my-4 flex items-center justify-between">
              <div class="my-auto text-sm font-normal text-gray-700">
                {{ t("common.allowMultipleFiles") }}
              </div>
              <UISwitch
                v-model:value="
                  store.elements[props.selectedIndex].isMultipleFile
                "
                size="small"
                @update:value="
                  () =>
                    store.markCustomFieldUpdated(
                      store.elements[props.selectedIndex]
                    )
                "
              />
            </div>
            <div v-if="store.elements[props.selectedIndex].isMultipleFile">
              <div class="mb-2 mt-4 text-sm font-normal text-gray-700">
                {{ t("common.maxFileLimit") }}
              </div>
              <UIInput
                id="max-file-limit"
                v-model:value="store.elements[props.selectedIndex].maxFileSize"
                @update:value="
                  () =>
                    store.markCustomFieldUpdated(
                      store.elements[props.selectedIndex],
                      false
                    )
                "
                @blur="store.addToHistory()"
              />
            </div>
          </div>
          <div
            v-if="store.elements[selectedIndex]?.dataType === 'RADIO'"
            class="my-4 flex items-center justify-between"
          >
            <div class="my-auto block text-sm font-medium text-gray-700">
              {{ t("common.allowOtherOption") }}
            </div>
            <UISwitch
              v-model:value="
                store.elements[props.selectedIndex].allowCustomOption
              "
              @update:value="store.addToHistory()"
            />
          </div>
          <div
            v-if="
              [
                'SINGLE_OPTIONS',
                'MULTIPLE_OPTIONS',
                'RADIO',
                'CHECKBOX',
              ].includes(store.elements[selectedIndex]?.dataType) &&
              store.elements[selectedIndex]?.model !== 'opportunity'
            "
            class="my-4 flex items-center justify-between"
          >
            <div class="my-auto block text-sm font-medium text-gray-700">
              {{ t("common.enableCalculations") }}
            </div>
            <UISwitch
              v-model:value="
                store.elements[props.selectedIndex].enableCalculations
              "
              @update:value="updateEnableCalculations"
            />
          </div>
          <div
            v-if="
              ['RADIO', 'CHECKBOX'].includes(
                store.elements[selectedIndex]?.dataType
              )
            "
            class="form-group mt-4"
          >
            <div class="flex justify-between pb-2">
              <div
                class="switch-handle mb-1 block text-sm font-medium text-gray-700"
              >
                {{ t("common.spreadToColumns") }}
              </div>
              <UISwitch
                :value="getSpreadColumns"
                @update:value="updateSpreadColumns"
              ></UISwitch>
            </div>

            <UIInputGroup v-if="getSpreadColumns">
              <UIInputNumber
                id="fieldQueryKey"
                size="large"
                class="w-100"
                :min="1"
                :max="10"
                :default-value="getDefaultNumberOfColumns"
                :show-button="false"
                :model-value="getNumberOfColumns"
                @update:model-value="updateNumberOfColumns"
                @blur="store.addToHistory()"
              ></UIInputNumber
              ><UIInputGroupLabel>{{ t("common.columns") }}</UIInputGroupLabel>
            </UIInputGroup>
          </div>
          <UIAlert
            v-if="
              [
                'TEXTBOX_LIST',
                'SINGLE_OPTIONS',
                'MULTIPLE_OPTIONS',
                'RADIO',
                'CHECKBOX',
              ].includes(store.elements[selectedIndex]?.dataType) &&
              (store.elements[selectedIndex]?.customEdited ||
                !store.elements[selectedIndex]?.custom)
            "
            id="warning-message"
            type="warning"
          >
            <template #content>
              {{ t("common.swapWarningMessageForm") }}
            </template>
          </UIAlert>
        </UITabPane>
      </UITabs>
    </div>
  </div>
</template>

<style scoped>
/* Strip UIInput borders for inline-heading look */
:deep(#imageLabelInline.n-input),
:deep(#htmlLabelInline.n-input) {
  border: 0 !important;
  box-shadow: none !important;
}
:deep(#imageLabelInline .n-input__border),
:deep(#imageLabelInline .n-input__state-border),
:deep(#htmlLabelInline .n-input__border),
:deep(#htmlLabelInline .n-input__state-border) {
  display: none !important;
}
:deep(#imageLabelInline .n-input__input-el),
:deep(#htmlLabelInline .n-input__input-el) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
<style>
.field-name-sidebar {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center !important;
  margin-bottom: 1rem !important;
}

#custom-sidebar-form {
  font-size: 14px;
}

.standard-input-ghl {
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.query-key {
  display: flex;
  align-items: baseline;
}

.form-group {
  margin-bottom: 1rem;
}

.hidden-field {
  margin-top: 1rem;
}

.form-check-req-hid {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.arrow-button {
  margin-bottom: 0px;
  cursor: pointer;
}

.alignment {
  display: flex;
  margin-bottom: 10px;
}

.alignment-div {
  margin-right: 10px;
}

.custom-icons {
  color: #38a0db;
  margin: 0 10px;
  cursor: pointer;
}
/* Hide only this sidebar's tab nav (Content/Options), not nested UITabs e.g. PaymentSettings General/Product */
.hide-outer-tabs > .n-tabs > .n-tabs-nav .n-tabs-nav-scroll-wrapper {
  display: none !important;
}

.button-sidebar-wrapper {
  margin: -15px;
}
</style>
